import { useMemo } from 'react'
import qrcode from 'qrcode-generator'
import { barsFor, isMatrix, type BarcodeFormat } from '../lib/barcode.ts'

/**
 * ШТРИХКОД НА ЭКРАНЕ
 *
 * Рисуется в чёрно-белом виде независимо от темы приложения: сканер
 * на кассе ищет чёрные полосы на белом, и «ночная» карта с белыми
 * полосами на тёмном он не прочтёт.
 *
 * Номер всегда подписан под кодом. Сканеры ломаются, экраны бликуют,
 * и возможность продиктовать номер голосом спасает чаще, чем кажется.
 */

export function Barcode({
  value,
  format,
  height = 90,
}: {
  value: string
  format: BarcodeFormat
  height?: number
}) {
  const bars = useMemo(() => barsFor(value, format), [value, format])

  const matrix = useMemo(() => {
    if (!isMatrix(format)) return null

    try {
      // 0 — подобрать размер самому; «M» — обычный уровень избыточности:
      // код остаётся читаемым на бликующем экране, но не разрастается
      const qr = qrcode(0, 'M')
      qr.addData(value)
      qr.make()

      const count = qr.getModuleCount()
      const cells: boolean[][] = []
      for (let row = 0; row < count; row += 1) {
        const line: boolean[] = []
        for (let column = 0; column < count; column += 1) line.push(qr.isDark(row, column))
        cells.push(line)
      }
      return cells
    } catch {
      // Слишком длинная строка для QR — покажем хотя бы номер
      return null
    }
  }, [value, format])

  if (matrix) {
    const count = matrix.length
    // Поля вокруг кода обязательны: без них сканер не находит границу
    const quiet = 2
    const size = count + quiet * 2

    return (
      <div className="barcode">
        <svg viewBox={`0 0 ${size} ${size}`} shapeRendering="crispEdges" role="img" aria-label={value}>
          <rect width={size} height={size} fill="#fff" />
          {matrix.map((line, row) =>
            line.map((dark, column) =>
              dark ? (
                <rect
                  key={`${row}-${column}`}
                  x={column + quiet}
                  y={row + quiet}
                  width={1}
                  height={1}
                  fill="#000"
                />
              ) : null,
            ),
          )}
        </svg>
        <span className="barcode-value">{value}</span>
      </div>
    )
  }

  if (!bars) {
    // Нарисовать нечем — крупный номер лучше пустоты: его наберут руками
    return (
      <div className="barcode">
        <span className="barcode-number">{value}</span>
      </div>
    )
  }

  const total = bars.widths.reduce((sum, width) => sum + width, 0)
  const quiet = 10
  let x = quiet
  let black = bars.startsBlack

  const rects = bars.widths.map((width, index) => {
    const rect = black ? (
      <rect key={index} x={x} y={0} width={width} height={height} fill="#000" />
    ) : null
    x += width
    black = !black
    return rect
  })

  return (
    <div className="barcode">
      <svg
        viewBox={`0 0 ${total + quiet * 2} ${height}`}
        preserveAspectRatio="none"
        shapeRendering="crispEdges"
        role="img"
        aria-label={value}
        style={{ height }}
      >
        <rect width={total + quiet * 2} height={height} fill="#fff" />
        {rects}
      </svg>
      <span className="barcode-value">{value}</span>
    </div>
  )
}
